import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react"
import { IconButton, Tooltip } from "@mui/material"
import LightbulbIcon from "@mui/icons-material/Lightbulb"
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined"
import { connectBoardDevice, requestBoardDevice } from "../utils/bluetooth"
import useStickyState from "../hooks/useStickyState"

export const BoardConnectionContext = createContext({
  characteristic: null,
  connecting: false,
  reconnecting: false,
  autoReconnect: true,
  setAutoReconnect: () => {},
  connect: async () => null,
  disconnect: () => {},
})

// Reconnect backoff after the board drops the link (a sleeping phone, a
// power-cycled board): 1s, 2s, 4s, ... capped, then give up.
const RECONNECT_DELAYS_MS = [1000, 2000, 4000, 8000, 15000, 30000]

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

/**
 * One Web Bluetooth connection to the kilter board, shared by every page.
 * The app bar's lightbulb is the connect/disconnect control; pages read
 * `characteristic` and write to the wall while it's non-null.
 *
 * Once the user has chosen a device, a dropped link is re-established
 * automatically (no chooser needed) unless the persisted auto-reconnect
 * setting is off, and the screen is kept awake while connected so a
 * phone left by the wall keeps acting as the board's controller.
 */
export function BoardConnectionProvider({ children }) {
  const [characteristic, setCharacteristic] = useState(null)
  const [connecting, setConnecting] = useState(false)
  const [reconnecting, setReconnecting] = useState(false)
  const [autoReconnect, setAutoReconnect] = useStickyState(
    true,
    "boardAutoReconnect",
  )
  // Read from the disconnect listener, which is registered once per device
  const autoReconnectRef = useRef(autoReconnect)
  autoReconnectRef.current = autoReconnect
  // The device the user picked, kept so reconnects skip the chooser
  const deviceRef = useRef(null)
  // Set by an explicit disconnect so the drop event doesn't reconnect
  const wantedRef = useRef(false)
  const reconnectingRef = useRef(false)

  const reconnect = useCallback(async () => {
    const device = deviceRef.current
    if (!device || reconnectingRef.current) {
      return
    }
    reconnectingRef.current = true
    setConnecting(true)
    setReconnecting(true)
    try {
      for (const delay of RECONNECT_DELAYS_MS) {
        await sleep(delay)
        if (!wantedRef.current || deviceRef.current !== device) {
          return
        }
        try {
          const next = await connectBoardDevice(device)
          setCharacteristic(next)
          return
        } catch (err) {
          console.warn("Board reconnect failed, retrying:", err)
        }
      }
      console.error("Gave up reconnecting to the board")
    } finally {
      reconnectingRef.current = false
      setConnecting(false)
      setReconnecting(false)
    }
  }, [])

  const connect = useCallback(async () => {
    if (characteristic || connecting) {
      return characteristic
    }
    setConnecting(true)
    try {
      const device = await requestBoardDevice()
      if (!device) {
        return null
      }
      deviceRef.current = device
      wantedRef.current = true
      device.addEventListener("gattserverdisconnected", () => {
        setCharacteristic(null)
        if (
          wantedRef.current &&
          autoReconnectRef.current &&
          deviceRef.current === device
        ) {
          reconnect()
        }
      })
      const next = await connectBoardDevice(device)
      setCharacteristic(next)
      return next
    } catch (err) {
      console.error("Bluetooth connect failed:", err)
      return null
    } finally {
      setConnecting(false)
    }
  }, [characteristic, connecting, reconnect])

  const disconnect = useCallback(() => {
    wantedRef.current = false
    const device = deviceRef.current
    deviceRef.current = null
    device?.gatt?.disconnect()
    setCharacteristic(null)
  }, [])

  useScreenWakeLock(Boolean(characteristic))

  return (
    <BoardConnectionContext.Provider
      value={{
        characteristic,
        connecting,
        reconnecting,
        autoReconnect,
        setAutoReconnect,
        connect,
        disconnect,
      }}
    >
      {children}
    </BoardConnectionContext.Provider>
  )
}

/**
 * Hold a screen wake lock while `active`. Mobile browsers throttle timers
 * and drop Bluetooth once the screen sleeps, which would silently stop a
 * phone from relaying gym changes to the board. The lock is released by
 * the browser whenever the tab is hidden, so it's re-requested on return.
 */
function useScreenWakeLock(active) {
  useEffect(() => {
    if (!active || typeof navigator === "undefined" || !navigator.wakeLock) {
      return
    }
    let lock = null
    let cancelled = false
    const request = async () => {
      if (cancelled || document.visibilityState !== "visible") {
        return
      }
      try {
        lock = await navigator.wakeLock.request("screen")
      } catch (err) {
        console.warn("Screen wake lock unavailable:", err)
      }
    }
    request()
    document.addEventListener("visibilitychange", request)
    return () => {
      cancelled = true
      document.removeEventListener("visibilitychange", request)
      lock?.release().catch(() => {})
    }
  }, [active])
}

export const useBoardConnection = () => useContext(BoardConnectionContext)

/**
 * App-bar lightbulb: unlit while disconnected, glowing while connected,
 * pulsing while the chooser is open or a dropped link is being restored.
 * Hidden in browsers without Web Bluetooth, where connecting can never
 * work.
 */
export function BoardConnectionIndicator({ subtitle }) {
  const { characteristic, connecting, connect, disconnect } =
    useBoardConnection()

  if (typeof navigator !== "undefined" && !("bluetooth" in navigator)) {
    return null
  }

  const title = characteristic
    ? `Board connected${subtitle ? ` — ${subtitle}` : ""} — tap to disconnect`
    : connecting
      ? "Connecting to board…"
      : "Connect to board"

  return (
    <Tooltip title={title}>
      <IconButton
        onClick={characteristic ? disconnect : connect}
        sx={{
          color: characteristic ? "#ffb74d" : "text.secondary",
          ...(characteristic && {
            filter: "drop-shadow(0 0 6px rgba(255, 183, 77, 0.7))",
          }),
          ...(connecting && {
            animation: "bulbPulse 1.2s ease-in-out infinite",
            "@keyframes bulbPulse": {
              "0%, 100%": { opacity: 1 },
              "50%": { opacity: 0.35 },
            },
          }),
        }}
      >
        {characteristic ? <LightbulbIcon /> : <LightbulbOutlinedIcon />}
      </IconButton>
    </Tooltip>
  )
}
